import { useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';
import { useRequests, REQ_TYPES, CHANNELS, CONTACT_ROLES, type Channel, type ReqType } from '../RequestsContext';
import RequestWorkspace from '../components/RequestWorkspace';

const chLabel: Record<string, string> = { phone: 'Call', email: 'Email', text: 'Text', website: 'Web', portal: 'Portal', verbal: 'Verbal' };
const stClass = (s: string) => (s === 'closed' ? 'st-completed' : s === 'escalated' || s === 'blocked' ? 'st-blocked' : s === 'waiting_client' ? 'st-acknowledged' : s === 'new' ? 'st-new' : 'st-in_progress');

export default function Intake() {
  const { demo } = useAuth();
  const { data } = useAccounts(demo);
  const R = useRequests();
  const clientNames = useMemo(() => (data?.accounts ?? []).filter((a) => a.type !== 'vendor').map((a) => a.name), [data]);

  const [source, setSource] = useState<Channel>('phone');
  const [client, setClient] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactRole, setContactRole] = useState('Buyer');
  const [type, setType] = useState<ReqType>('unclassified');
  const [summary, setSummary] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const create = () => {
    if (!client.trim() && !summary.trim()) return;
    const acct = (data?.accounts ?? []).find((a) => a.name.toLowerCase() === client.toLowerCase());
    const id = R.createRequest({
      source, clientId: acct?.id, clientName: client || 'New client', type,
      contact: contactName ? { name: contactName, role: contactRole } : undefined, summary,
    });
    setSummary(''); setContactName(''); setOpenId(id);
  };

  const open = R.requests.find((x) => x.id === openId) || null;
  const counts = {
    open: R.requests.filter((r) => r.status !== 'closed').length,
    waiting: R.requests.filter((r) => r.status === 'waiting_client').length,
    escalated: R.requests.filter((r) => r.status === 'escalated').length,
    closed: R.requests.filter((r) => r.status === 'closed').length,
  };

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Intake &amp; Requests</div><div className="page-sub">Capture every client request and run it through the full lifecycle to close.</div></div>
      </div>

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-label">Open Requests</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{counts.open}</div><div className="metric-note">in the pipeline</div></div>
        <div className="metric-card"><div className="metric-label">Waiting on Client</div><div className="metric-value" style={{ color: 'var(--yl)' }}>{counts.waiting}</div><div className="metric-note">missing info / approval</div></div>
        <div className="metric-card"><div className="metric-label">Escalated</div><div className="metric-value" style={{ color: 'var(--rd)' }}>{counts.escalated}</div><div className="metric-note">need attention</div></div>
        <div className="metric-card"><div className="metric-label">Closed</div><div className="metric-value" style={{ color: 'var(--gn)' }}>{counts.closed}</div><div className="metric-note">resolved</div></div>
      </div>

      <div className="grid sidebar-layout">
        <div className="card">
          <div className="card-header"><div><h3>New Request</h3><p>Phone, email, text, website, portal, or verbal.</p></div></div>
          <div className="card-body">
            <div className="form-grid">
              <div className="field"><label>Source</label><select value={source} onChange={(e) => setSource(e.target.value as Channel)}>{CHANNELS.map((c) => <option key={c} value={c}>{chLabel[c]}</option>)}</select></div>
              <div className="field"><label>Request type</label><select value={type} onChange={(e) => setType(e.target.value as ReqType)}><option value="unclassified">Classify later</option>{REQ_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}</select></div>
              <div className="field"><label>Client (match or new)</label><input list="intake-clients" value={client} onChange={(e) => setClient(e.target.value)} placeholder="Search or type new…" /><datalist id="intake-clients">{clientNames.slice(0, 200).map((n) => <option key={n} value={n} />)}</datalist></div>
              <div className="field"><label>Contact</label><input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Buyer name" /></div>
              <div className="field"><label>Contact role</label><select value={contactRole} onChange={(e) => setContactRole(e.target.value)}>{CONTACT_ROLES.map((x) => <option key={x}>{x}</option>)}</select></div>
              <div className="field full"><label>Request details</label><textarea value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What does the client need?" /></div>
            </div>
            <button className="btn primary" style={{ marginTop: 12 }} onClick={create}>Create request →</button>
          </div>
        </div>

        <div className="card pad">
          <div className="section-h">Request Lifecycle</div>
          <ol className="lifecycle">
            {['Create intake', 'Match client', 'Add contact', 'Classify', 'Create & assign task', 'Acknowledge', 'Communicate', 'Request missing info', 'Follow-up', 'Update status', 'Attach files', 'Escalate', 'Close', 'Log interactions'].map((s, i) => (
              <li key={i}><span className="lc-n">{i + 1}</span>{s}</li>
            ))}
          </ol>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><div><h3>Request Queue</h3><p>Open any request to run the full lifecycle.</p></div></div>
        <div className="table-wrap"><table>
          <thead><tr><th>ID</th><th>Client</th><th>Type</th><th>Source</th><th>Tasks</th><th>Status</th><th>Created</th></tr></thead>
          <tbody>
            {R.requests.map((r) => (
              <tr key={r.id} onClick={() => setOpenId(r.id)}>
                <td><strong>{r.id}</strong></td>
                <td>{r.clientName}</td>
                <td><span className="tag pr-standard">{r.type}</span></td>
                <td><span className="chan-badge ch-email">{chLabel[r.source]}</span></td>
                <td>{r.tasks.length}</td>
                <td><span className={`tag ${stClass(r.status)}`}>{r.status.replace('_', ' ')}</span></td>
                <td>{r.created}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {open && <RequestWorkspace request={open} onClose={() => setOpenId(null)} />}
    </>
  );
}
